import { Edit } from "iconsax-reactjs"
import { ChevronDown, Loader2, Plus } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Select } from "@/components/shared/ui/select"
import { Badge, Button, Checkbox, Image } from "@/components/ui"
import { SkuSelection } from "@/features/jobs/explore/components/campaign/actions/sku-selection"
import { EAffiliateCampaignType } from "@/modules/helpers/enum"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { formatAddress as formatAddressUtil } from "@/modules/helpers/utils/campaign-form"
import type { Address } from "@/modules/types"
import type { ProductCampaignItem, SaleProp } from "@/modules/types/jobs/campaign-form"

// Product Info Section
interface ProductInfoSectionProps {
  productImage?: string
  productTitle?: string
  shopName?: string
}

export function ProductInfoSection({
  productImage,
  productTitle,
  shopName
}: ProductInfoSectionProps) {
  return (
    <div className="bg-background rounded-xl p-3 mb-4">
      <div className="flex gap-3">
        {productImage && (
          <Image
            src={productImage}
            alt={productTitle || "Product"}
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm text-black mb-1 line-clamp-2">{productTitle || ""}</h3>
          <p className="text-xs text-primary">{shopName || ""}</p>
        </div>
      </div>
    </div>
  )
}

// Address Section
interface AddressSectionProps {
  addresses: Address[]
  selectedAddress: Address | null
  onAddressClick: (address: Address) => void
  onEditClick: (address: Address) => void
  onAddNew: () => void
  formatAddress?: (address: Address) => string
  errors?: {
    address?: string
    fullName?: string
    phoneNumber?: string
  }
  light?: boolean
}

export function AddressSection({
  addresses,
  selectedAddress,
  onAddressClick,
  onEditClick,
  onAddNew,
  formatAddress = formatAddressUtil,
  errors,
  light
}: AddressSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showArrow, setShowArrow] = useState(false)

  const checkScrollBottom = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5 // 5px threshold
      setShowArrow(!isAtBottom && addresses.length > 0)
    }
  }, [addresses.length])

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (scrollElement) {
      checkScrollBottom()
      scrollElement.addEventListener("scroll", checkScrollBottom)
      return () => {
        scrollElement.removeEventListener("scroll", checkScrollBottom)
      }
    }
  }, [checkScrollBottom])

  useEffect(() => {
    // Re-check when addresses change
    checkScrollBottom()
  }, [addresses, checkScrollBottom])

  return (
    <div>
      <div className="flex flex-col gap-1 mb-2">
        <h2 className="text-sm font-normal text-black">Địa chỉ nhận hàng</h2>
        <p className="text-xs text-gray-600">
          Bạn có thể theo dõi đơn hàng này tại mục "Mẫu miễn phí" trên TikTok. Nếu không thể gửi mẫu
          qua TikTok, một số nhãn hàng sẽ gửi đơn ngoài theo địa chỉ bên dưới.
        </p>
        {/* <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="touch-manipulation">
              <Info size={16} className="text-black" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3 text-sm" side="bottom" align="center">
            <p className="text-black font-normal text-xs">
              Chỉ áp dụng cho các chiến dịch không gửi qua TikTok
            </p>
          </PopoverContent>
        </Popover> */}
      </div>

      <div className="relative">
        <div ref={scrollRef} className="space-y-2 mb-4 overflow-scroll">
          {addresses.length === 0 ? (
            <div className="text-center py-4 text-sm text-gray-500">Chưa có địa chỉ nào</div>
          ) : (
            addresses.map((address) => {
              const isSelected = selectedAddress?.id === address.id
              return (
                <div
                  key={address.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onAddressClick(address)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      onAddressClick(address)
                    }
                  }}
                  className={cn(
                    "w-full text-left rounded-xl p-3 border transition-colors cursor-pointer",
                    isSelected
                      ? "border-primary bg-primary-50/30"
                      : errors?.address
                        ? "border-red-500"
                        : "border-gray-200",
                    {
                      "!bg-white": light
                    }
                  )}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 text-gray-500">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-600 font-semibold">
                          {address.fullName}
                        </span>
                        <span className="text-gray-400">|</span>
                        <span className="text-sm">
                          (+84) {address?.phoneNumber?.replace(/^0/, "")}
                        </span>
                      </div>
                      <p className="text-xs">{formatAddress(address)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onEditClick(address)
                      }}
                      className="p-1 rounded hover:bg-gray-200"
                    >
                      <Edit size={16} className="text-primary-800" />
                    </button>
                  </div>
                  {address.isDefault && (
                    <Badge className="bg-primary !text-white border-0 text-[10px] px-3 py-1 font-normal">
                      Mặc định
                    </Badge>
                  )}
                </div>
              )
            })
          )}
        </div>
        {showArrow && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none">
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-1 shadow-lg">
              <ChevronDown size={16} className="text-gray-600" />
            </div>
          </div>
        )}
      </div>

      {errors?.address && <p className="text-xs text-red-500 mb-2">{errors.address}</p>}

      <Button
        onClick={onAddNew}
        className="w-full h-10 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-medium"
      >
        <Plus size={18} className="mr-2" />
        Thêm địa chỉ mới
      </Button>
    </div>
  )
}

// Form Type Section
interface FormTypeSectionProps {
  contentTypes: EAffiliateCampaignType[]
  onToggle: (type: EAffiliateCampaignType) => void
  error?: string
}

export function FormTypeSection({ contentTypes, onToggle, error }: FormTypeSectionProps) {
  return (
    <div>
      <h2 className="text-sm font-normal mb-3">Hình thức</h2>
      <div className="flex gap-2">
        <div
          role="button"
          tabIndex={0}
          onClick={() => onToggle(EAffiliateCampaignType.VIDEO)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onToggle(EAffiliateCampaignType.VIDEO)
            }
          }}
          className={cn(
            "w-fit flex items-center gap-3 px-3 bg-gray-50 rounded-lg border transition-colors h-10 cursor-pointer",
            error ? "border-red-500" : "border-gray-200"
          )}
        >
          <Checkbox
            checked={contentTypes.includes(EAffiliateCampaignType.VIDEO)}
            onCheckedChange={() => onToggle(EAffiliateCampaignType.VIDEO)}
            className="pointer-events-none"
          />
          <span className="text-sm text-black">Video</span>
        </div>
      </div>
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
    </div>
  )
}

// Quantity Section
interface QuantitySectionProps {
  value: string
  onChange: (value: string) => void
  error?: string
}

export function QuantitySection({ value, onChange, error }: QuantitySectionProps) {
  return (
    <div>
      <div className="flex justify-between">
        <div className="flex items-start flex-col justify-between mb-2 gap-1">
          <h2 className="text-sm font-normal">
            Số lượng video có thể hoàn thành khi nhận được mẫu?
          </h2>
          <span className="text-xs text-gray-600">(Tối đa 3 video)</span>
          <span className="text-xs text-gray-600">
            Số lượng video càng cao, khả năng được chọn càng lớn
          </span>
        </div>
        <Select
          value={value}
          onChange={onChange}
          options={["1", "2", "3"].map((num) => ({
            label: num,
            value: num
          }))}
          placeholder="Chọn"
          className={cn(
            "w-fit min-w-[100px] h-10 bg-gray-50 rounded-lg text-sm",
            error ? "border-red-500" : "border-gray-200"
          )}
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}

// SKU Selection Section
interface SkuSelectionSectionProps {
  saleProps: SaleProp[]
  selectedSaleProp: Record<string, string>
  onSelect: (propValueId: string, propId: string) => void
  selectedSkus?: Record<string, Record<string, string>>
  onSelectMultiple?: (productId: string, propId: string, propValueId: string) => void
  error?: string
  productCampaignSaleProps?: ProductCampaignItem[]
  skus?: any[]
  skuHiddens?: any[]
}

export function SkuSelectionSection({
  saleProps,
  selectedSaleProp,
  onSelect,
  selectedSkus,
  onSelectMultiple,
  error,
  productCampaignSaleProps,
  skus,
  skuHiddens
}: SkuSelectionSectionProps) {
  return (
    <SkuSelection
      saleProps={saleProps}
      selectedSaleProp={selectedSaleProp}
      onSelect={onSelect}
      productCampaignSaleProps={productCampaignSaleProps}
      selectedSkus={selectedSkus}
      onSelectMultiple={onSelectMultiple}
      error={error}
      skus={skus}
      skuHiddens={skuHiddens}
    />
  )
}

// Form Action Buttons
interface FormActionButtonsProps {
  onCancel: () => void
  onConfirm: () => void
  isPending: boolean
  onRequestSample?: () => Promise<boolean>
  isCheckingSample?: boolean
  isSampleRegistered?: boolean
  hasKolSubmissionId?: boolean
}

export function FormActionButtons({
  onConfirm,
  isPending,
  isSampleRegistered
}: FormActionButtonsProps) {
  return (
    <div className="p-4 border-t border-gray-200 bg-white flex flex-col gap-3">
      <p className="text-[11px] text-gray-500 text-left italic px-2">
        * Tùy vào tình hình thực tế mà Nhãn hàng có thể gửi sản phẩm mẫu khác với loại mà bạn đã
        chọn
      </p>
      <div className="flex gap-2 w-full">
        <Button
          onClick={isSampleRegistered ? undefined : onConfirm}
          disabled={isPending || isSampleRegistered}
          className={cn(
            "flex-1 !h-12 rounded-xl font-medium transition-all flex items-center justify-center gap-2",
            isPending || isSampleRegistered
              ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
              : "bg-primary-800 text-white active:scale-[0.98]"
          )}
        >
          {isPending && <Loader2 size={16} className="animate-spin" />}
          {isSampleRegistered ? "Đã đăng ký" : isPending ? "Đang đợi" : "Xác nhận đăng ký"}
        </Button>
      </div>
    </div>
  )
}
